/**
 * 买家端统一请求封装（fetch 实现，无 axios 依赖）
 * ---------------------------------------------------------------------------
 * - baseURL：统一前缀 /api（由 vite dev proxy 转发到网关 :8080，生产走 nginx 同前缀）
 * - 请求拦截：自动注入 Authorization: Bearer <token>（token 取自 localStorage['bp_buyer_token']）
 * - 响应拦截：解析后端统一 Result 信封 { code, message, data }；
 *   · code !== 200 → 抛出 ApiError（直接带后端 message，不向用户暴露 HTTP 栈）
 *   · HTTP 401 → 清登录态；仅当请求曾携带 token（会话失效）才整页跳转 /buyer/login，匿名公开调用由调用方 .catch 降级
 * - 成功返回 data（已拆信封）
 */
import { tr } from '@/i18n';


/** 后端统一响应信封 */
export interface ApiResult<T> {
  code: number;
  message: string;
  data: T;
  timestamp?: number;
  success?: boolean;
}

/** 业务/HTTP 异常：message 为可直显给用户的文案（已由后端或本封装收敛，不含堆栈） */
export class ApiError extends Error {
  code: number;
  constructor(message: string, code: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

/** localStorage 键名（与 BuyerAuthContext 保持一致） */
export const TOKEN_KEY = 'bp_buyer_token';

/** 网关路由前缀：前端只写业务路径（如 /buyer/login），由本封装拼成 /api/buyer/login */
const API_PREFIX = '/api';

function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * 核心请求方法。
 * @param path    业务路径（不含 /api 前缀，如 '/buyer/login'）
 * @param options fetch 原生配置（method / body / headers 等）
 */
export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  // Let the browser add the multipart boundary for FormData uploads.
  if (!(typeof FormData !== 'undefined' && options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    headers,
  });

  // 401：token 失效或未登录
  if (response.status === 401) {
    clearToken();
    // 仅当本次请求携带过 token（曾登录、会话失效）才整页跳登录；
    // 匿名公开访问（无 token，如未登录浏览首页/商品）不强制跳转，
    // 交由调用方 .catch 优雅降级（首页会渲染空列表而非被冲到登录页）
    if (token && typeof window !== 'undefined') {
      window.location.href = '/buyer/login';
    }
    throw new ApiError(tr('api.sessionExpired'), 401);
  }

  let body: ApiResult<T>;
  try {
    body = (await response.json()) as ApiResult<T>;
  } catch {
    throw new ApiError(tr('api.badResponse', { status: response.status }), response.status);
  }

  // 业务失败：直接抛后端 message（不暴露 HTTP 栈），由调用方 catch 后展示
  if (body.code !== 200) {
    throw new ApiError(body.message || tr('api.requestFailed', { code: body.code }), body.code);
  }

  return body.data;
}

/** GET */
export function get<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'GET' });
}

/** POST（data 自动 JSON 序列化；data 为空不传 body） */
export function post<T>(path: string, data?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: 'POST',
    body: data == null ? undefined : JSON.stringify(data),
  });
}

/** PUT */
export function put<T>(path: string, data?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: 'PUT',
    body: data == null ? undefined : JSON.stringify(data),
  });
}

/** DELETE */
export function del<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE' });
}

export interface UploadedBuyerFile {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

/** Upload one attachment for an inquiry conversation. */
export function uploadBuyerFile(file: File, bizId: string): Promise<UploadedBuyerFile> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bizType', 'inquiry_attachment');
  formData.append('bizId', bizId);
  return apiRequest<UploadedBuyerFile>('/file/upload', { method: 'POST', body: formData });
}
